import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "./api";
import { Icon } from "./Icons";
import { VISTE, aggrega, fmt, labelData, labelDataBreve } from "./riepilogoUtils";

const C = {
  blue:    "#1565C0",
  grey:    "#546E7A",
  light:   "#F5F5F5",
  border:  "#E0E0E0",
  text:    "#212121",
  textSub: "#757575",
  error:   "#B71C1C",
  grid:    "#ECEFF1",
};
const FONT = "Roboto, 'Segoe UI', Arial, sans-serif";

// Le 3 metriche pro-capite (colonna sinistra, a linee): valore = numeratore / persone.
const METRICHE = [
  { num: "colli_totali_prodotti", label: "Colli pro capite totali",               color: "#1565C0" },
  { num: "colli_carico_cnt",      label: "Colli pro capite per carico container",  color: "#00897B" },
  { num: "colli_senza_pr",        label: "Colli pro capite per collo completo",    color: "#EF6C00" },
];

// I 3 valori assoluti corrispondenti (colonna destra, a barre). Stesso colore per riga.
const METRICHE_ABS = [
  { num: "colli_totali_prodotti", label: "Colli totali prodotti",  color: "#1565C0" },
  { num: "colli_carico_cnt",      label: "Colli per carico CNT",   color: "#00897B" },
  { num: "colli_senza_pr",        label: "Colli prodotti senza PR", color: "#EF6C00" },
];

// Etichetta compatta per l'asse x a seconda della vista.
function xLabel(row, vista) {
  if (vista === "giornaliera") return labelDataBreve(row.data);
  if (vista === "settimanale") return "S" + (row.label.match(/Sett\. (\d+)/)?.[1] ?? "");
  if (vista === "mensile")     return row.label.slice(0, 3) + " " + row.label.slice(-2); // "Giu 26"
  return row.label; // annuale → "2026"
}
function xLabelFull(row, vista) {
  return vista === "giornaliera" ? labelData(row.data) : row.label;
}

// Valore pro-capite di una riga per una metrica (null se persone = 0).
function valore(row, metrica) {
  const p = row.persone || 0;
  return p > 0 ? (row[metrica.num] || 0) / p : null;
}

// Valore assoluto (il numeratore stesso: giornaliero o media del periodo).
function valoreAssoluto(row, metrica) {
  return row[metrica.num] || 0;
}

// "Bel" massimo per l'asse y (arrotondato verso l'alto).
function niceMax(v) {
  if (!v || v <= 0) return 1;
  const pot = Math.pow(10, Math.floor(Math.log10(v)));
  const norm = v / pot;
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return step * pot;
}

// Misura la dimensione (px) dell'elemento referenziato.
function useSize() {
  const ref = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const cr = entries[0].contentRect;
      setSize({ w: cr.width, h: cr.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size];
}

// ── Grafico a linee (SVG in pixel reali, serie singola) ────────────────────────
function LineChart({ points, color, title, note }) {
  const [hover, setHover] = useState(null); // indice punto
  const [wrapRef, { w, h }] = useSize();
  const svgRef = useRef(null);

  const padL = 56, padR = 18, padT = 12, padB = 26;
  const plotW = Math.max(0, w - padL - padR);
  const plotH = Math.max(0, h - padT - padB);

  const n = points.length;
  const valid = points.map(p => p.y).filter(y => y !== null && !isNaN(y));
  const yMax = niceMax(valid.length ? Math.max(...valid) * 1.1 : 1);

  const xAt = (i) => n <= 1 ? padL + plotW / 2 : padL + (i / (n - 1)) * plotW;
  const yAt = (v) => padT + plotH - (v / yMax) * plotH;

  // Path della linea, interrotto sui null.
  let d = "", pendingMove = true;
  points.forEach((p, i) => {
    if (p.y === null || isNaN(p.y)) { pendingMove = true; return; }
    d += `${pendingMove ? "M" : "L"} ${xAt(i).toFixed(1)} ${yAt(p.y).toFixed(1)} `;
    pendingMove = false;
  });

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => f * yMax);
  const stepX = n <= 14 ? 1 : Math.ceil(n / 12);

  const onMove = (e) => {
    if (n === 0 || plotW <= 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    const px = e.clientX - rect.left;
    let i = n <= 1 ? 0 : Math.round(((px - padL) / plotW) * (n - 1));
    i = Math.max(0, Math.min(n - 1, i));
    setHover(i);
  };

  const hp = hover !== null ? points[hover] : null;
  const hasHover = hp && hp.y !== null && !isNaN(hp.y);
  const ready = w > 0 && h > 0;

  return (
    <div style={{
      background: "#fff", borderRadius: 8, border: `1px solid ${C.border}`,
      boxShadow: "0 1px 4px rgba(0,0,0,0.07)", padding: "10px 16px 6px",
      display: "flex", flexDirection: "column", flex: 1, minHeight: 0,
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4, flexShrink: 0 }}>
        <span style={{ width: 12, height: 12, borderRadius: 3, background: color, display: "inline-block" }} />
        <span style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{title}</span>
        {note && <span style={{ marginLeft: "auto", fontSize: 12, color: C.textSub }}>{note}</span>}
      </div>

      <div ref={wrapRef} style={{ flex: 1, minHeight: 0, position: "relative" }}>
        {ready && n === 0 && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: C.textSub }}>
            Nessun dato
          </div>
        )}
        {ready && n > 0 && (
          <svg
            ref={svgRef}
            width={w} height={h}
            style={{ display: "block", fontFamily: FONT }}
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
          >
            {/* Griglia + etichette y */}
            {yTicks.map((v, i) => (
              <g key={i}>
                <line x1={padL} x2={w - padR} y1={yAt(v)} y2={yAt(v)} stroke={C.grid} strokeWidth="1" />
                <text x={padL - 8} y={yAt(v) + 4} textAnchor="end" fontSize="11" fill={C.textSub}>{fmt(v)}</text>
              </g>
            ))}

            {/* Etichette x */}
            {points.map((p, i) => (i % stepX === 0 || i === n - 1) && (
              <text key={i} x={xAt(i)} y={h - padB + 16} textAnchor="middle" fontSize="10.5" fill={C.textSub}>
                {p.x}
              </text>
            ))}

            {/* Crosshair hover */}
            {hasHover && (
              <line x1={xAt(hover)} x2={xAt(hover)} y1={padT} y2={padT + plotH} stroke={color} strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
            )}

            {/* Linea */}
            <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

            {/* Marker */}
            {points.map((p, i) => (p.y !== null && !isNaN(p.y)) && (
              <circle key={i} cx={xAt(i)} cy={yAt(p.y)} r={hover === i ? 5 : 3.5}
                fill="#fff" stroke={color} strokeWidth="2" />
            ))}

            {/* Tooltip */}
            {hasHover && (() => {
              const tx = xAt(hover), ty = yAt(hp.y);
              const boxW = 150, boxH = 38;
              const bx = Math.max(padL, Math.min(w - padR - boxW, tx - boxW / 2));
              const by = Math.max(padT, ty - boxH - 10);
              return (
                <g>
                  <rect x={bx} y={by} width={boxW} height={boxH} rx="6" fill={C.text} opacity="0.92" />
                  <text x={bx + 10} y={by + 15} fontSize="11" fill="#B0BEC5">{hp.xFull}</text>
                  <text x={bx + 10} y={by + 31} fontSize="14" fontWeight="700" fill="#fff">{fmt(hp.y)}</text>
                </g>
              );
            })()}
          </svg>
        )}
      </div>
    </div>
  );
}

// Path di una barra con angoli superiori arrotondati, base sulla linea zero.
function barPath(x, y, w, hh, r) {
  r = Math.max(0, Math.min(r, w / 2, hh));
  return `M${x},${y + hh} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + hh} Z`;
}

// ── Grafico a barre (SVG in pixel reali, serie singola) ────────────────────────
function BarChart({ points, color, title, note }) {
  const [hover, setHover] = useState(null);
  const [wrapRef, { w, h }] = useSize();
  const svgRef = useRef(null);

  const padL = 56, padR = 18, padT = 12, padB = 26;
  const plotW = Math.max(0, w - padL - padR);
  const plotH = Math.max(0, h - padT - padB);

  const n = points.length;
  const valid = points.map(p => p.y).filter(y => y !== null && !isNaN(y));
  const yMax = niceMax(valid.length ? Math.max(...valid) * 1.1 : 1);

  const slot = n > 0 ? plotW / n : plotW;
  const barW = Math.max(1, Math.min(slot - 2, 46));
  const xLeft = (i) => padL + i * slot + (slot - barW) / 2;
  const xCenter = (i) => padL + i * slot + slot / 2;
  const yAt = (v) => padT + plotH - (v / yMax) * plotH;
  const y0 = padT + plotH;

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => f * yMax);
  const stepX = n <= 14 ? 1 : Math.ceil(n / 12);

  const onMove = (e) => {
    if (n === 0 || plotW <= 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    const px = e.clientX - rect.left;
    let i = Math.floor((px - padL) / slot);
    i = Math.max(0, Math.min(n - 1, i));
    setHover(i);
  };

  const hp = hover !== null ? points[hover] : null;
  const hasHover = hp && hp.y !== null && !isNaN(hp.y);
  const ready = w > 0 && h > 0;

  return (
    <div style={{
      background: "#fff", borderRadius: 8, border: `1px solid ${C.border}`,
      boxShadow: "0 1px 4px rgba(0,0,0,0.07)", padding: "10px 16px 6px",
      display: "flex", flexDirection: "column", flex: 1, minHeight: 0,
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4, flexShrink: 0 }}>
        <span style={{ width: 12, height: 12, borderRadius: 3, background: color, display: "inline-block" }} />
        <span style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{title}</span>
        {note && <span style={{ marginLeft: "auto", fontSize: 12, color: C.textSub }}>{note}</span>}
      </div>

      <div ref={wrapRef} style={{ flex: 1, minHeight: 0, position: "relative" }}>
        {ready && n === 0 && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: C.textSub }}>
            Nessun dato
          </div>
        )}
        {ready && n > 0 && (
          <svg
            ref={svgRef}
            width={w} height={h}
            style={{ display: "block", fontFamily: FONT }}
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
          >
            {/* Griglia + etichette y */}
            {yTicks.map((v, i) => (
              <g key={i}>
                <line x1={padL} x2={w - padR} y1={yAt(v)} y2={yAt(v)} stroke={C.grid} strokeWidth="1" />
                <text x={padL - 8} y={yAt(v) + 4} textAnchor="end" fontSize="11" fill={C.textSub}>{fmt(v)}</text>
              </g>
            ))}

            {/* Etichette x */}
            {points.map((p, i) => (i % stepX === 0 || i === n - 1) && (
              <text key={i} x={xCenter(i)} y={h - padB + 16} textAnchor="middle" fontSize="10.5" fill={C.textSub}>
                {p.x}
              </text>
            ))}

            {/* Barre */}
            {points.map((p, i) => {
              const v = (p.y === null || isNaN(p.y)) ? 0 : p.y;
              const hh = y0 - yAt(v);
              if (hh <= 0) return null;
              return (
                <path key={i} d={barPath(xLeft(i), yAt(v), barW, hh, Math.min(4, barW / 2))}
                  fill={color} opacity={hover === null || hover === i ? 1 : 0.5} />
              );
            })}

            {/* Etichetta operatori sopra ogni barra (solo se c'è spazio) */}
            {slot >= 30 && points.map((p, i) => {
              const v = (p.y === null || isNaN(p.y)) ? 0 : p.y;
              const yTop = yAt(v);
              return (
                <text key={i} x={xCenter(i)} y={Math.max(padT + 9, yTop - 5)} textAnchor="middle"
                  fontSize="10" fill={C.textSub}>
                  {fmt(p.op)} op.
                </text>
              );
            })}

            {/* Tooltip */}
            {hasHover && (() => {
              const ty = yAt(hp.y);
              const boxW = 156, boxH = 54;
              const bx = Math.max(padL, Math.min(w - padR - boxW, xCenter(hover) - boxW / 2));
              const by = Math.max(padT, ty - boxH - 8);
              return (
                <g>
                  <rect x={bx} y={by} width={boxW} height={boxH} rx="6" fill={C.text} opacity="0.92" />
                  <text x={bx + 10} y={by + 15} fontSize="11" fill="#B0BEC5">{hp.xFull}</text>
                  <text x={bx + 10} y={by + 31} fontSize="14" fontWeight="700" fill="#fff">{fmt(hp.y)}</text>
                  <text x={bx + 10} y={by + 47} fontSize="11" fill="#B0BEC5">{fmt(hp.op)} operatori</text>
                </g>
              );
            })()}
          </svg>
        )}
      </div>
    </div>
  );
}

// ── Componente principale ─────────────────────────────────────────────────────
export default function Dashboard() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [vista, setVista]     = useState("giornaliera");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.getRiepilogo();
      setRows(d);
      setError("");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const isDaily = vista === "giornaliera";
  const serieRows = isDaily ? rows : aggrega(rows, vista);

  // Punti per una metrica, dato un estrattore di valore (pro-capite o assoluto).
  // `op` = numero operatori (media del periodo in aggregazione).
  const chartPoints = (metrica, estrai) => serieRows.map(r => ({
    x: xLabel(r, vista),
    xFull: xLabelFull(r, vista),
    y: estrai(r, metrica),
    op: r.persone || 0,
  }));

  return (
    <div style={{
      fontFamily: FONT, background: C.light,
      height: "calc(100vh - var(--app-header-h, 150px) - 8px)",
      display: "flex", flexDirection: "column", padding: "14px 20px", boxSizing: "border-box", overflow: "hidden",
    }}>

      {/* Toolbar: switch vista */}
      <div style={{
        background: "#fff", borderRadius: 8, padding: "10px 16px", marginBottom: 12, flexShrink: 0,
        border: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
      }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: 0.6, marginRight: 6 }}>
          Aggregazione
        </span>
        {VISTE.map(v => (
          <button
            key={v.id}
            onClick={() => setVista(v.id)}
            style={{
              background: vista === v.id ? C.blue : "#fff",
              color: vista === v.id ? "#fff" : C.grey,
              border: `1.5px solid ${vista === v.id ? C.blue : C.border}`, borderRadius: 6,
              padding: "7px 16px", cursor: "pointer", fontFamily: FONT, fontWeight: 700, fontSize: 14,
            }}
          >
            {v.label}
          </button>
        ))}
        <span style={{ marginLeft: "auto", fontSize: 13, color: C.textSub }}>
          {isDaily ? `${serieRows.length} giorni` : `${serieRows.length} periodi · media dei valori`}
        </span>
      </div>

      {error && (
        <div style={{
          background: "#FFEBEE", border: "1px solid #EF9A9A", color: C.error,
          padding: "10px 14px", borderRadius: 6, marginBottom: 12, fontWeight: 600, flexShrink: 0,
          display: "flex", alignItems: "center",
        }}>
          <Icon name="warning" size={16} color={C.error} style={{ marginRight: 6 }} />{error}
          <button onClick={() => setError("")} style={{ marginLeft: "auto", cursor: "pointer", background: "none", border: "none", fontWeight: 700, fontSize: 18, color: C.error }}>×</button>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 60, textAlign: "center", color: C.textSub }}>Caricamento…</div>
      ) : (
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 12 }}>
          {METRICHE.map((m, i) => (
            <div key={m.num} style={{ flex: 1, minHeight: 0, display: "flex", gap: 12 }}>
              {/* Sinistra: pro-capite (linee) */}
              <LineChart
                title={m.label}
                color={m.color}
                note={isDaily ? "valore giornaliero" : "media del periodo"}
                points={chartPoints(m, valore)}
              />
              {/* Destra: valore assoluto (barre) */}
              <BarChart
                title={METRICHE_ABS[i].label}
                color={METRICHE_ABS[i].color}
                note={isDaily ? "valore giornaliero" : "media del periodo"}
                points={chartPoints(METRICHE_ABS[i], valoreAssoluto)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
